import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Users, CheckCircle, Building2 } from 'lucide-react';
import { teamService } from '@/services/team.service';
import type { TeamInvitation } from '@/types';
import { cn } from '@/utils/cn';

export const PendingInvitationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);

  useEffect(() => {
    teamService
      .getMyInvitations()
      .then((inv) => {
        if (inv.length === 0) {
          navigate('/', { replace: true });
        } else {
          setInvitations(inv);
        }
      })
      .catch(() => navigate('/', { replace: true }))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleAccept = async (inv: TeamInvitation) => {
    setProcessing(inv.id);
    try {
      await teamService.acceptInvitation(inv.token);
      toast.success(`Joined ${inv.organization_name || 'the team'}!`);
      setInvitations((prev) => prev.filter((i) => i.id !== inv.id));
    } catch {
      toast.error('Failed to accept invitation');
    } finally {
      setProcessing(null);
    }
  };

  const handleDecline = async (inv: TeamInvitation) => {
    setProcessing(inv.id);
    try {
      await teamService.declineInvitation(inv.token);
      setInvitations((prev) => prev.filter((i) => i.id !== inv.id));
    } catch {
      toast.error('Failed to decline invitation');
    } finally {
      setProcessing(null);
    }
  };

  // All invitations handled -> go to dashboard
  useEffect(() => {
    if (!loading && invitations.length === 0) {
      navigate('/', { replace: true });
    }
  }, [invitations, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFFDF7]">
        <div className="w-6 h-6 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFDF7] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-center -space-x-1 mb-6">
          <img src="/logo-icon.svg" alt="ChatCup" className="h-12" />
          <img src="/logo-name.svg" alt="ChatCup.ai" className="h-7" />
        </div>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-50 border-2 border-[#1a1a2e] mb-4">
            <Users className="w-7 h-7 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Pending Invitations</h1>
          <p className="text-sm text-gray-500 mt-1.5">
            You have {invitations.length} pending team invitation{invitations.length !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="space-y-3">
          {invitations.map((inv) => (
            <div
              key={inv.id}
              className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[4px_4px_0px_#1a1a2e] p-5"
            >
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-50 border-2 border-[#1a1a2e] shrink-0">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm">
                    {inv.organization_name || 'Unknown Organization'}
                  </p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Invited as <span className="font-medium capitalize text-gray-700">{inv.role}</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Expires {new Date(inv.expires_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => handleAccept(inv)}
                  disabled={processing === inv.id}
                  className={cn(
                    'flex-1 h-9 flex items-center justify-center gap-1.5',
                    'bg-blue-500 text-white text-sm font-bold rounded-xl border-2 border-[#1a1a2e]',
                    'shadow-[3px_3px_0px_#1a1a2e] hover:shadow-[4px_4px_0px_#1a1a2e]',
                    'hover:translate-x-[-1px] hover:translate-y-[-1px] transition-all duration-200',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  {processing === inv.id ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Accept
                    </>
                  )}
                </button>
                <button
                  onClick={() => handleDecline(inv)}
                  disabled={processing === inv.id}
                  className={cn(
                    'flex-1 h-9 flex items-center justify-center',
                    'bg-white text-gray-700 text-sm font-bold rounded-xl',
                    'border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]',
                    'hover:shadow-[4px_4px_0px_#1a1a2e] hover:translate-x-[-1px] hover:translate-y-[-1px]',
                    'transition-all duration-200',
                    'disabled:opacity-50 disabled:cursor-not-allowed'
                  )}
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-6">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};
